'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { X, LogIn, UserPlus } from 'lucide-react';
import Link from 'next/link';

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showModal?: boolean;
}

export default function AuthGuard({ children, fallback, showModal = false }: AuthGuardProps) {
  const { user, loading } = useAuthContext();
  const router = useRouter();
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      if (showModal) {
        setShowLoginModal(true);
      } else {
        router.push('/login');
      }
    }
  }, [user, loading, router, showModal]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    if (fallback) {
      return <>{fallback}</>;
    }

    if (showLoginModal) {
      return (
        <>
          {/* Modal Overlay */}
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-slate-800/90 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl max-w-md w-full">
              <div className="text-center">
                {/* Close Button */}
                <button
                  onClick={() => router.push('/')}
                  className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                {/* Icon */}
                <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <LogIn className="w-8 h-8 text-blue-400" />
                </div>

                {/* Title */}
                <h2 className="text-2xl font-bold text-white mb-4">
                  Sign In Required
                </h2>

                {/* Message */}
                <p className="text-slate-300 mb-8">
                  You must be logged in to access this page. Please sign in to continue.
                </p>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Link href="/login" className="block">
                    <Button className="w-full bg-blue-600 hover:bg-blue-500 text-white">
                      <LogIn className="w-4 h-4 mr-2" />
                      Sign In
                    </Button>
                  </Link>
                  
                  <Link href="/signup" className="block">
                    <Button variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-700">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create Account
                    </Button>
                  </Link>
                </div>

                {/* Back to Home */}
                <button
                  onClick={() => router.push('/')}
                  className="text-slate-400 hover:text-white text-sm mt-6 transition-colors"
                >
                  ← Back to Home
                </button>
              </div>
            </div>
          </div>

          {/* Blurred Background */}
          <div className="blur-sm">
            {children}
          </div>
        </>
      );
    }

    return null;
  }

  return <>{children}</>;
} 